import { RULES, SCORE, VIEW } from './config.js';
import { createLoop } from './loop.js';
import {
  addScore,
  createSession,
  resetAfterLife,
  serveBall,
  showToast,
  startRound,
} from './session.js';
import { fire as fireAction, updatePlay } from './play.js';
import { createShake } from '../systems/particles.js';
import { DOH_PHASE } from '../entities/doh.js';
import { TOTAL_ROUNDS } from '../levels/levels.js';
import { UI } from '../render/palette.js';

/**
 * The state machine that stitches rounds, screens, audio and the render loop
 * together. Gameplay itself lives in play.js.
 */

export const STATE = Object.freeze({
  TITLE: 'title',
  READY: 'ready',
  PLAYING: 'playing',
  PAUSED: 'paused',
  LIFE_LOST: 'lifeLost',
  ROUND_CLEAR: 'roundClear',
  WARP: 'warp',
  BOSS_DEFEAT: 'bossDefeat',
  GAME_OVER: 'gameOver',
  VICTORY: 'victory',
});

const SILENT = new Proxy({}, { get: () => () => {} });

export function createGame({ renderer, storage, sfx, music, hud, overlays, reducedMotion }) {
  const session = createSession({ reducedMotion });
  const shake = createShake({ reducedMotion });

  let state = STATE.TITLE;
  let stateTimer = 0;
  let input = null;
  let attract = false;
  let resumeState = STATE.PLAYING;

  const events = {
    onBallLost: () => handleBallLost(),
    onPaddleDestroyed: () => handleBallLost(true),
    onRoundClear: () => (attract ? restartAttract() : enter(STATE.ROUND_CLEAR)),
    onWarp: () => (attract ? restartAttract() : enter(STATE.WARP)),
    onDohDefeated: () => !attract && enter(STATE.BOSS_DEFEAT),
    onExtraLife: () => {
      if (attract) return;
      sfx.extraLife();
      showToast(session, 'Extra Vaus');
      hud.announce('Extra Vaus earned');
    },
    onCapsuleTaken: (code) => !attract && hud.announce(`Capsule ${code} collected`),
  };

  const fx = { sfx, shake, events, targetX: VIEW.W / 2 };

  function enter(next) {
    state = next;
    stateTimer = 0;

    switch (next) {
      case STATE.TITLE:
        attract = true;
        session.scoring = false;
        session.score = 0;
        session.lives = RULES.startingLives;
        session.nextExtraLife = RULES.firstExtraLife;
        startRound(session, 1);
        launchHeldBalls();
        session.banner = null;
        overlays.show('title', { high: String(storage.get('highScore')).padStart(6, '0') });
        music.play('title');
        break;

      case STATE.READY:
        attract = false;
        session.scoring = true;
        overlays.hide();
        session.paddleVisible = true;
        session.banner = {
          title: `ROUND ${session.round}`,
          sub: session.level.name,
          accent: session.isBoss ? UI.danger : UI.accent,
        };
        sfx.roundStart();
        hud.announce(`Round ${session.round}, ${session.level.name}`);
        music.play(session.isBoss ? 'boss' : null);
        if (session.isBoss) sfx.dohAppear();
        break;

      case STATE.PLAYING:
        overlays.hide();
        session.banner = null;
        if (session.balls.some((ball) => ball.stuck)) {
          showToast(session, 'Press fire to launch');
        }
        break;

      case STATE.PAUSED:
        overlays.show('pause');
        sfx.pause(true);
        break;

      case STATE.LIFE_LOST:
        session.paddleVisible = false;
        session.balls.length = 0;
        session.bolts.length = 0;
        session.dohShots.length = 0;
        session.lives -= 1;
        sfx.lifeLost();
        shake.add(9);
        session.particles.burst(session.paddle.cx, session.paddle.y, {
          count: 24,
          color: '#ced6e2',
          speed: 210,
          life: 0.8,
          size: 3,
        });
        hud.announce(
          session.lives > 0 ? `Vaus lost. ${session.lives} remaining` : 'Last Vaus lost',
        );
        break;

      case STATE.ROUND_CLEAR:
        session.banner = { title: 'ROUND CLEAR', sub: `+${SCORE.roundClear} POINTS` };
        addScore(session, SCORE.roundClear);
        session.stats.rounds += 1;
        sfx.roundClear();
        music.play(null);
        storage.unlockRound(session.round + 1);
        hud.announce('Round cleared');
        break;

      case STATE.WARP:
        session.banner = { title: 'WARP', sub: `+${SCORE.warpBonus} BONUS`, accent: UI.accentAlt };
        session.balls.length = 0;
        session.stats.rounds += 1;
        sfx.warp();
        hud.announce('Warp gate cleared');
        break;

      case STATE.BOSS_DEFEAT:
        session.banner = null;
        session.balls.length = 0;
        session.dohShots.length = 0;
        sfx.dohDefeat();
        shake.add(14);
        hud.announce('DOH defeated');
        break;

      case STATE.GAME_OVER:
        session.banner = null;
        music.play(null);
        sfx.gameOver();
        commitScore();
        overlays.show('gameOver', {
          score: String(session.score).padStart(6, '0'),
          round: `${session.round} / ${TOTAL_ROUNDS}`,
          bricks: String(session.stats.bricks),
        });
        break;

      case STATE.VICTORY:
        session.banner = null;
        music.play(null);
        sfx.victory();
        commitScore();
        overlays.show('victory', {
          score: String(session.score).padStart(6, '0'),
          bricks: String(session.stats.bricks),
          capsules: String(session.stats.capsules),
        });
        break;

      default:
        break;
    }
  }

  function commitScore() {
    if (storage.recordScore(session.score)) {
      showToast(session, 'New high score');
      hud.announce('New high score');
    }
  }

  /** Attract mode never ends: reset the demo board and keep bouncing. */
  function restartAttract() {
    startRound(session, 1);
    launchHeldBalls();
  }

  function launchHeldBalls() {
    for (const ball of session.balls) {
      if (!ball.stuck) continue;
      ball.stuck = false;
      ball.stuckTimer = 0;
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.2;
      ball.vx = Math.cos(angle) * ball.speed;
      ball.vy = Math.sin(angle) * ball.speed;
    }
  }

  function handleBallLost(instant = false) {
    if (attract) {
      serveBall(session);
      launchHeldBalls();
      return;
    }
    if (state !== STATE.PLAYING) return;
    if (instant) session.balls.length = 0;
    enter(STATE.LIFE_LOST);
  }

  function advanceRound() {
    const next = session.round + 1;
    if (next > TOTAL_ROUNDS) {
      enter(STATE.VICTORY);
      return;
    }
    startRound(session, next);
    enter(STATE.READY);
  }

  function update(dt) {
    stateTimer += dt;
    fx.targetX = input ? input.resolve(dt, session.paddle) : session.paddle.cx;
    fx.sfx = attract ? SILENT : sfx;

    switch (state) {
      case STATE.TITLE:
        // Attract mode: the paddle plays itself behind the title panel.
        fx.targetX = session.balls[0] ? session.balls[0].x : VIEW.W / 2;
        updatePlay(session, dt, fx);
        break;

      case STATE.READY:
        updateReady(dt);
        break;

      case STATE.PLAYING:
        updatePlay(session, dt, fx);
        break;

      case STATE.LIFE_LOST:
        session.particles.update(dt);
        if (stateTimer > 1.5) {
          if (session.lives <= 0) enter(STATE.GAME_OVER);
          else {
            resetAfterLife(session);
            enter(STATE.READY);
          }
        }
        break;

      case STATE.ROUND_CLEAR:
        session.particles.update(dt);
        if (stateTimer > RULES.clearDuration) advanceRound();
        break;

      case STATE.WARP:
        updateWarpExit(dt);
        break;

      case STATE.BOSS_DEFEAT:
        updateBossDefeat(dt);
        break;

      default:
        session.particles.update(dt);
        break;
    }

    shake.update(dt);
  }

  function updateReady(dt) {
    session.particles.update(dt);
    session.field?.tick(dt);
    const paddle = session.paddle;
    const half = paddle.w / 2;
    paddle.cx = Math.max(16 + half, Math.min(VIEW.W - 16 - half, fx.targetX));
    for (const ball of session.balls) {
      ball.x = paddle.cx + ball.stuckOffset;
      ball.y = paddle.y - ball.r - 1;
    }
    if (stateTimer > RULES.readyDuration) enter(STATE.PLAYING);
  }

  function updateWarpExit(dt) {
    session.particles.update(dt);
    session.paddle.cx += 320 * dt;
    if (stateTimer > RULES.warpDuration) advanceRound();
  }

  function updateBossDefeat(dt) {
    session.particles.update(dt);
    const doh = session.doh;
    if (doh) {
      doh.deathTimer += dt;
      if (Math.random() < dt * 9) {
        const px = doh.cx + (Math.random() - 0.5) * doh.w;
        const py = doh.y + Math.random() * doh.h;
        session.particles.burst(px, py, {
          count: 12,
          color: Math.random() < 0.5 ? '#9a9083' : '#f0803c',
          speed: 190,
          life: 0.7,
          size: 3,
        });
        shake.add(3);
      }
    }
    if (stateTimer > 3.6) {
      if (session.doh) {
        addScore(session, SCORE.dohDefeated);
        session.doh = null;
      }
      enter(STATE.VICTORY);
    }
  }

  function render() {
    // HUD writes happen once per drawn frame, not once per physics step.
    hud.update(session, storage.get('highScore'));
    renderer.draw(session, { shake: shake.offset() });
  }

  const loop = createLoop({ update, render });

  return {
    session,

    get state() {
      return state;
    },

    setInput(next) {
      input = next;
    },

    start() {
      enter(STATE.TITLE);
      loop.start();
    },

    newGame(fromRound = 1) {
      attract = false;
      session.scoring = true;
      session.score = 0;
      session.lives = RULES.startingLives;
      session.nextExtraLife = RULES.firstExtraLife;
      session.stats.bricks = 0;
      session.stats.capsules = 0;
      session.stats.rounds = 0;
      shake.clear();
      startRound(session, fromRound);
      enter(STATE.READY);
    },

    fire() {
      if (state === STATE.TITLE || state === STATE.GAME_OVER || state === STATE.VICTORY) return;
      if (state === STATE.READY) {
        enter(STATE.PLAYING);
        fireAction(session, fx);
        return;
      }
      if (state !== STATE.PLAYING) return;
      fireAction(session, fx);
    },

    togglePause() {
      if (state === STATE.PAUSED) {
        overlays.hide();
        sfx.pause(false);
        state = resumeState;
        stateTimer = 0;
        return;
      }
      if (state !== STATE.PLAYING && state !== STATE.READY) return;
      resumeState = state;
      enter(STATE.PAUSED);
    },

    /** Called when the tab is hidden so the game never runs unattended. */
    autoPause() {
      if (state === STATE.PLAYING) {
        resumeState = STATE.PLAYING;
        enter(STATE.PAUSED);
      }
    },

    quitToTitle() {
      enter(STATE.TITLE);
    },

    get isBoss() {
      return session.doh?.phase === DOH_PHASE.ACTIVE;
    },

    stop() {
      loop.stop();
    },
  };
}
